import type { CellKeyDownEvent, Column, GridApi, GridOptions, ICellEditorParams } from "ag-grid-enterprise";

/**
 * Spreadsheet-style keyboard behaviour shared by every editable grid, so data entry feels the
 * same on each page.
 */

// First column the user can type into on a row. A leading read-only Errors column is passed over
// rather than made unnavigable, since keyboard users still need to reach the error text.
export function firstEditableColumn(api: GridApi, rowIndex = 0): Column | undefined {
  const row = api.getDisplayedRowAtIndex(rowIndex);
  if (!row) return undefined;

  return api
    .getAllDisplayedColumns()
    .find(
      (col) => !col.getColId().startsWith("ag-Grid-") && !col.getColDef().suppressNavigable && col.isCellEditable(row)
    );
}

export function focusCell(api: GridApi, rowIndex: number, column: Column): void {
  api.ensureIndexVisible(rowIndex);
  api.ensureColumnVisible(column);
  api.setFocusedCell(rowIndex, column);
  if (api.getGridOption("cellSelection")) {
    api.clearCellSelection();
    api.addCellRange({ rowStartIndex: rowIndex, rowEndIndex: rowIndex, columns: [column] });
  }
}

function isPlainEnter(key: unknown): key is KeyboardEvent {
  return (
    key instanceof KeyboardEvent &&
    key.key === "Enter" &&
    !key.shiftKey &&
    !key.ctrlKey &&
    !key.metaKey &&
    !key.altKey
  );
}

// AG Grid's own Enter navigation never calls the navigateToNextCell callback, so the carriage
// return runs after it: once the native move down has landed, focus is re-pointed at the first
// editable cell of that row. Skipped while an editor is still open (blocked commit) and on the last
// row, where native Enter stays put. AG Grid delivers these callbacks a tick after its synchronous
// move, so focus can show one frame at the same column first. Accepted: avoiding it means taking
// over Enter and reimplementing AG Grid's commit, validation and navigation.
function moveToNextRowStart(api: GridApi, rowIndex: number): void {
  if (api.getEditingCells().length > 0) return;

  const nextIndex = rowIndex + 1;
  if (api.getFocusedCell()?.rowIndex !== nextIndex) return;

  const column = firstEditableColumn(api, nextIndex);
  if (column) focusCell(api, nextIndex, column);
}

function enterMovesToNextRowStart(event: CellKeyDownEvent): void {
  if (!isPlainEnter(event.event)) return;
  if (event.rowIndex === null || event.node.rowPinned || event.column.getColId().startsWith("ag-Grid-")) return;

  moveToNextRowStart(event.api, event.rowIndex);
}

// Popup editors get Enter from AG Grid's popup wrapper, which commits and moves down without
// dispatching cellKeyDown. Call it from the editor's own keydown, which runs before the wrapper,
// hence the deferral.
export function popupEnterMovesToNextRowStart(params: ICellEditorParams, event: KeyboardEvent): void {
  if (!isPlainEnter(event) || params.node.rowPinned) return;

  const { api, node, rowIndex } = params;
  setTimeout(() => {
    if (!api.isDestroyed()) moveToNextRowStart(api, node.rowIndex ?? rowIndex);
  });
}

// Enter moves to the first editable cell of the next row (carriage return), editing or not;
// typing starts an edit. Arrow keys and Tab keep AG Grid's behaviour.
export const EXCEL_ENTER_NAVIGATION: Pick<
  GridOptions,
  "enterNavigatesVertically" | "enterNavigatesVerticallyAfterEdit" | "onCellKeyDown"
> = {
  enterNavigatesVertically: true,
  enterNavigatesVerticallyAfterEdit: true,
  onCellKeyDown: (event) => {
    if ("column" in event) enterMovesToNextRowStart(event);
  },
};

// Returns whether a cell took focus: a read-only grid has nothing to type into.
export function focusFirstCell(api: GridApi): boolean {
  const column = firstEditableColumn(api);
  if (!column) return false;

  focusCell(api, 0, column);
  return true;
}

// setFocusedCell scrolls the page to the cell, so only a first row already fully on screen is
// focused; a grid scrolled into view from below is left alone.
function firstRowOnScreen(api: GridApi, gridElement: HTMLElement): boolean {
  const { headerHeight, rowHeight } = api.getSizesForCurrentTheme();
  const top = gridElement.getBoundingClientRect().top;

  return top >= 0 && top + headerHeight + rowHeight <= window.innerHeight;
}

// For onFirstDataRendered: focuses the first time the grid is on screen, so a grid mounted hidden
// is handled on reveal. Unrequested, it never takes focus from anything; `requested` is for a grid
// the user just asked for by a click, which leaves focus on what they clicked.
export function autoFocusFirstCell(
  api: GridApi,
  gridElement: HTMLElement,
  { requested = false }: { requested?: boolean } = {}
): void {
  const observer = new IntersectionObserver((entries) => {
    if (!entries.some((entry) => entry.isIntersecting)) return;

    observer.disconnect();
    if (api.isDestroyed()) return;
    // IntersectionObserver counts visibility:hidden as intersecting, but a hidden cell can't take
    // DOM focus; a grid revealed by a click is focused by its reveal site instead.
    if (getComputedStyle(gridElement).visibility === "hidden") return;
    if (requested) {
      focusFirstCell(api);
      return;
    }

    const active = document.activeElement;
    if (active && active !== document.body) return;
    if (firstRowOnScreen(api, gridElement)) focusFirstCell(api);
  });
  observer.observe(gridElement);
  api.addEventListener("gridPreDestroyed", () => observer.disconnect());
}
